import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '~/Services/Service';
import CardFiltros from './components/CardFiltros';
import ModalAlterarVencimento from './components/ModalAlterarVencimento';
import ModalCapOcorrencia from './components/ModalCapOcorrencia';
import ModalConfirmacaoSenha from './components/ModalConfirmacaoSenha';
import ModalSituacaoPagFor from './components/ModalSituacaoPagFor';
import TabelaResultados from './components/TabelaResultados';
import { obterCorLinha } from './helpers/obterCorLinha';

const INICIAL_FILTROS = {
  codigoGrupoEmpresa: null,
  codigoConta: null,
  dataInicio: '',
  dataFim: '',
  tipoData: 'digitacao',
  status: 'X',
  tipoDespesa: true,
  tipoProjeto: false,
  tipoComissao: false,
  tipoSienge: false,
  conferencia: 'todos',
  docNF: false,
  docGuia: false,
  docRecibo: false,
  docNFS: false,
  workflow: true,
  somenteWorkflow: false,
  apenasAbertos: false,
  pagamentosGestaoPessoas: false,
  rpa: false,
  mei: false,
  meiOpcao: 'todos',
  favorecido: '',
  codigoChave: null,
  codigoUsuario: null,
  codigoDepartamento: null,
  acaoUsuario: null,
};

const INICIAL_PAGINACAO = {
  orderBy: 'Data',
  orderDir: 'DESC',
  pageSize: 20,
  pageNumber: 1,
};

const RESPOSTA_VAZIA = {
  chaves: [],
  total: 0,
  totalizadores: {},
  page_number: 1,
  page_size: 20,
};

const normalizarResposta = (res) => ({
  chaves: res && res.chaves ? res.chaves : [],
  total: res && res.total ? res.total : 0,
  totalizadores: res && res.totalizadores ? res.totalizadores : {},
  page_number: res && res.page_number ? res.page_number : 1,
  page_size: res && res.page_size ? res.page_size : 20,
});

const AutorizaDespesa = () => {
  const [filtros, setFiltros] = useState(INICIAL_FILTROS);
  const [dados, setDados] = useState(RESPOSTA_VAZIA);
  const [paginacao, setPaginacao] = useState(INICIAL_PAGINACAO);
  const [carregando, setCarregando] = useState(false);
  const [selecionadas, setSelecionadas] = useState(new Set());
  const [modalSenha, setModalSenha] = useState({ show: false, acao: null, mensagem: '' });
  const [modalVencimento, setModalVencimento] = useState({ show: false, sequencia: null });
  const [modalOcorrencia, setModalOcorrencia] = useState({ show: false, sequencia: null });
  const [modalPagFor, setModalPagFor] = useState({ show: false, sequencia: null });

  const chavesOrdenadas = useMemo(() => dados.chaves || [], [dados.chaves]);

  const buscar = async (paginacaoOverride) => {
    const pag = paginacaoOverride || paginacao;
    if (
      !filtros.tipoDespesa &&
      !filtros.tipoProjeto &&
      !filtros.tipoComissao &&
      !filtros.tipoSienge
    ) {
      toast.warn('Selecione ao menos um tipo de chave');
      return;
    }
    setCarregando(true);
    try {
      const res = await api.listarAutorizaDespesa(filtros, pag);
      setDados(normalizarResposta(res));
      setSelecionadas(new Set());
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao buscar chaves';
      toast.error(msg);
    } finally {
      setCarregando(false);
    }
  };

  const limparFiltros = () => {
    setFiltros(INICIAL_FILTROS);
    setPaginacao(INICIAL_PAGINACAO);
    setDados(RESPOSTA_VAZIA);
    setSelecionadas(new Set());
  };

  const abrirModalSenha = (mensagem, acao) => {
    setModalSenha({ show: true, mensagem, acao });
  };

  const aoConfirmarAprovar = () => {
    if (selecionadas.size === 0) {
      toast.warn('Selecione ao menos uma chave');
      return;
    }
    const totalSelecionado = dados.chaves
      .filter((c) => selecionadas.has(c.sequencia))
      .reduce((acc, c) => acc + Number(c.valor || 0), 0);
    abrirModalSenha(
      `Confirma aprovação de ${selecionadas.size} chave(s) no valor total de ${totalSelecionado.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })}?`,
      async () => {
        await api.aprovarAutorizaDespesa(Array.from(selecionadas));
        toast.success('Aprovação concluída');
        await buscar();
      }
    );
  };

  const aoEstornar = (sequencia) => {
    abrirModalSenha(`Confirma estorno da aprovação da chave ${sequencia}?`, async () => {
      await api.estornarAprovacaoAutorizaDespesa(sequencia);
      toast.success('Estorno concluído');
      await buscar();
    });
  };

  const aoCancelar = (sequencia) => {
    abrirModalSenha(
      `Confirma cancelamento do lançamento ${sequencia}? Esta ação não pode ser desfeita.`,
      async () => {
        await api.cancelarLancamentoAutorizaDespesa(sequencia);
        toast.success('Lançamento cancelado');
        await buscar();
      }
    );
  };

  const aoVerificar = async (sequencia) => {
    try {
      await api.verificarPagamentoAutorizaDespesa(sequencia);
      toast.success('Pagamento verificado');
      await buscar();
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao verificar';
      toast.error(msg);
    }
  };

  const aoDevolver = async (sequencia) => {
    try {
      await api.devolverAoDigitadorAutorizaDespesa(sequencia);
      toast.success('Devolvido ao digitador');
      await buscar();
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao devolver';
      toast.error(msg);
    }
  };

  const mudarPaginacao = (novaPaginacao) => {
    setPaginacao(novaPaginacao);
    buscar(novaPaginacao);
  };

  const toggleSelecionada = (seq) => {
    const ns = new Set(selecionadas);
    if (ns.has(seq)) ns.delete(seq);
    else ns.add(seq);
    setSelecionadas(ns);
  };

  return (
    <div className="container-fluid p-3">
      <h4>Autoriza Despesa/Projetos</h4>

      <CardFiltros
        filtros={filtros}
        onChange={setFiltros}
        onBuscar={() => buscar({ ...paginacao, pageNumber: 1 })}
        onLimpar={limparFiltros}
        carregando={carregando}
      />

      <TabelaResultados
        chaves={chavesOrdenadas}
        total={dados.total}
        totalizadores={dados.totalizadores}
        paginacao={paginacao}
        onPaginacaoChange={mudarPaginacao}
        selecionadas={selecionadas}
        onToggleSelecionada={toggleSelecionada}
        onSelecionarTodas={(ids) => setSelecionadas(new Set(ids))}
        onAprovar={aoConfirmarAprovar}
        onEstornar={aoEstornar}
        onCancelar={aoCancelar}
        onVerificar={aoVerificar}
        onDevolver={aoDevolver}
        onAlterarVencimento={(sequencia) => setModalVencimento({ show: true, sequencia })}
        onAbrirOcorrencia={(sequencia) => setModalOcorrencia({ show: true, sequencia })}
        onAbrirSituacaoPagFor={(sequencia) => setModalPagFor({ show: true, sequencia })}
        obterCorLinha={obterCorLinha}
        carregando={carregando}
      />

      <ModalConfirmacaoSenha
        show={modalSenha.show}
        mensagem={modalSenha.mensagem}
        onConfirm={async () => {
          if (modalSenha.acao) await modalSenha.acao();
          setModalSenha({ show: false, acao: null, mensagem: '' });
        }}
        onCancel={() => setModalSenha({ show: false, acao: null, mensagem: '' })}
      />

      <ModalAlterarVencimento
        show={modalVencimento.show}
        sequencia={modalVencimento.sequencia}
        onClose={(salvou) => {
          setModalVencimento({ show: false, sequencia: null });
          if (salvou) buscar();
        }}
      />

      <ModalCapOcorrencia
        show={modalOcorrencia.show}
        sequencia={modalOcorrencia.sequencia}
        onClose={() => setModalOcorrencia({ show: false, sequencia: null })}
      />

      <ModalSituacaoPagFor
        show={modalPagFor.show}
        sequencia={modalPagFor.sequencia}
        onClose={() => setModalPagFor({ show: false, sequencia: null })}
      />
    </div>
  );
};

export default AutorizaDespesa;
